import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject, BehaviorSubject, throwError, of } from 'rxjs';
import { catchError, map, tap, finalize } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

// ============================================================
// Interfaces
// ============================================================

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content?: string;
  imageUrl?: string;
  imageCaption?: string;
}

export interface CreateSessionRequest {
  systemMessage?: string;
}

export interface CreateSessionResponse {
  sessionId: string;
  createdAt: string;
  expiresAt: string;
}

export interface SessionChatRequest {
  message: string;
}

export interface SessionChatResponse {
  response: string;
}

export interface ChatCompletionRequest {
  messages: ChatMessage[];
  maxTokens?: number;
  temperature?: number;
}

export interface ChatCompletionResponse {
  response: string;
}

export interface AddDocumentToSessionRequest {
  fileId: number;
  message?: string;
}

export interface AddDocumentToSessionResponse {
  success: boolean;
  message: string;
}

export interface DocumentFileAnalysisResponse {
  fileId: number;
  fileName: string;
  fileSizeBytes: number;
  companyId: number;
  status: string;
  detectedDocumentType: string;
  extractedData: string;
  confidence: number;
  uploadedAt: string;
  analyzedAt?: string;
  success: boolean;
  warnings?: string[];
}

export interface UploadWithSessionResponse {
  analysisResult: DocumentFileAnalysisResponse;
  addedToSession: boolean;
  sessionId: string;
}

export interface DocumentFileDetails {
  fileId: number;
  fileName: string;
  fileSizeBytes: number;
  contentType: string;
  status: string;
  detectedDocumentType?: string;
  uploadedAt: string;
  uploadedBy?: string;
  analyzedAt?: string;
  usedAt?: string;
  entityReference?: string;
}

export interface DocumentExtractionResponse {
  documentType: string;
  extractedData: Record<string, any>;
  confidence: number;
  rawResponse: string;
  success: boolean;
  error?: string;
}

export interface DocumentValidationResponse {
  isValid: boolean;
  confidence: number;
  validationResults: Record<string, any>;
  warnings: string[];
  issues: string[];
  recommendation: string;
  rawResponse: string;
  success: boolean;
  error?: string;
}

/** Represents a single streamed chunk event */
export interface StreamChunkEvent {
  type: 'chunk' | 'done' | 'error';
  content?: string;
  error?: string;
}

// ============================================================
// Service
// ============================================================

@Injectable({ providedIn: 'root' })
export class OpenAIChatService {

  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiConfig.corporateAPI.endpoint;

  // Session state
  private _sessionId$ = new BehaviorSubject<string | null>(null);
  private _sessionExpiresAt$ = new BehaviorSubject<string | null>(null);
  private _isStreaming$ = new BehaviorSubject<boolean>(false);

  /** Observable of the current session ID */
  readonly sessionId$ = this._sessionId$.asObservable();
  /** Observable indicating whether a streaming response is in progress */
  readonly isStreaming$ = this._isStreaming$.asObservable();

  get currentSessionId(): string | null {
    return this._sessionId$.value;
  }

  get isSessionActive(): boolean {
    const id = this._sessionId$.value;
    const expiresAt = this._sessionExpiresAt$.value;
    if (!id || !expiresAt) return false;
    return new Date(expiresAt) > new Date();
  }

  // ============================================================
  // Session Management
  // ============================================================

  createSession(systemMessage?: string): Observable<CreateSessionResponse> {
    return this.http.post<CreateSessionResponse>(
      `${this.baseUrl}/api/OpenAIChat/session`,
      { systemMessage } as CreateSessionRequest
    ).pipe(
      tap(res => {
        this._sessionId$.next(res.sessionId);
        this._sessionExpiresAt$.next(res.expiresAt);
      }),
      catchError(err => {
        console.error('[OpenAIChatService] createSession error:', err);
        return throwError(() => err);
      })
    );
  }

  deleteSession(sessionId?: string): Observable<any> {
    const id = sessionId || this._sessionId$.value;
    if (!id) return of({ success: true });

    return this.http.delete(`${this.baseUrl}/api/OpenAIChat/session/${id}`).pipe(
      tap(() => {
        if (!sessionId || sessionId === this._sessionId$.value) {
          this._sessionId$.next(null);
          this._sessionExpiresAt$.next(null);
        }
      }),
      catchError(err => {
        console.error('[OpenAIChatService] deleteSession error:', err);
        this._sessionId$.next(null);
        this._sessionExpiresAt$.next(null);
        return of({ success: true });
      })
    );
  }

  getSessionHistory(sessionId?: string): Observable<ChatMessage[]> {
    const id = sessionId || this._sessionId$.value;
    if (!id) return throwError(() => new Error('No active session'));

    return this.http.get<ChatMessage[]>(
      `${this.baseUrl}/api/OpenAIChat/session/${id}/history`
    ).pipe(
      catchError(err => {
        console.error('[OpenAIChatService] getSessionHistory error:', err);
        return throwError(() => err);
      })
    );
  }

  // ============================================================
  // Session-Based Chat
  // ============================================================

  sendSessionMessage(message: string, sessionId?: string): Observable<string> {
    const id = sessionId || this._sessionId$.value;
    if (!id) return throwError(() => new Error('No active session. Call createSession() first.'));

    return this.http.post<SessionChatResponse>(
      `${this.baseUrl}/api/OpenAIChat/session/${id}/chat`,
      { message } as SessionChatRequest
    ).pipe(
      map(res => res.response),
      catchError(err => {
        console.error('[OpenAIChatService] sendSessionMessage error:', err);
        return throwError(() => err);
      })
    );
  }

  streamSessionMessage(message: string, sessionId?: string): Subject<StreamChunkEvent> {
    const id = sessionId || this._sessionId$.value;
    const stream$ = new Subject<StreamChunkEvent>();

    if (!id) {
      stream$.next({ type: 'error', error: 'No active session. Call createSession() first.' });
      stream$.complete();
      return stream$;
    }

    this._isStreaming$.next(true);
    this.fetchStream(
      `${this.baseUrl}/api/OpenAIChat/session/${id}/chat-stream`,
      { message },
      stream$
    );

    return stream$;
  }

  // ============================================================
  // Stateless Chat
  // ============================================================

  chatCompletion(messages: ChatMessage[], maxTokens?: number, temperature?: number): Observable<string> {
    const body: ChatCompletionRequest = { messages, maxTokens, temperature };
    return this.http.post<ChatCompletionResponse>(
      `${this.baseUrl}/api/OpenAIChat/chat`,
      body
    ).pipe(
      map(res => res.response),
      catchError(err => {
        console.error('[OpenAIChatService] chatCompletion error:', err);
        return throwError(() => err);
      })
    );
  }

  chatCompletionStream(messages: ChatMessage[], maxTokens?: number, temperature?: number): Subject<StreamChunkEvent> {
    const stream$ = new Subject<StreamChunkEvent>();
    this._isStreaming$.next(true);

    this.fetchStream(
      `${this.baseUrl}/api/OpenAIChat/chat-stream`,
      { messages, maxTokens, temperature },
      stream$
    );

    return stream$;
  }

  // ============================================================
  // Document AI
  // ============================================================

  addDocumentToSession(fileId: number, companyId: number, message?: string, sessionId?: string): Observable<AddDocumentToSessionResponse> {
    const id = sessionId || this._sessionId$.value;
    if (!id) return throwError(() => new Error('No active session'));

    return this.http.post<AddDocumentToSessionResponse>(
      `${this.baseUrl}/api/OpenAIChat/session/${id}/document`,
      { fileId, message } as AddDocumentToSessionRequest,
      { headers: new HttpHeaders({ companyId: companyId.toString() }) }
    ).pipe(
      catchError(err => {
        console.error('[OpenAIChatService] addDocumentToSession error:', err);
        return throwError(() => err);
      })
    );
  }

  uploadDocument(
    companyId: number,
    file: File,
    options?: { targetEntityType?: string; userMessage?: string; sessionId?: string }
  ): Observable<DocumentFileAnalysisResponse | UploadWithSessionResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.targetEntityType) formData.append('targetEntityType', options.targetEntityType);
    if (options?.userMessage) formData.append('userMessage', options.userMessage);

    const sid = options?.sessionId || this._sessionId$.value;
    if (sid) formData.append('sessionId', sid);

    return this.http.post<DocumentFileAnalysisResponse | UploadWithSessionResponse>(
      `${this.baseUrl}/api/company/${companyId}/DocumentAI/upload`,
      formData
    ).pipe(
      catchError(err => {
        console.error('[OpenAIChatService] uploadDocument error:', err);
        return throwError(() => err);
      })
    );
  }

  getCompanyDocuments(companyId: number, status?: string): Observable<DocumentFileDetails[]> {
    let url = `${this.baseUrl}/api/company/${companyId}/DocumentAI/files`;
    if (status) url += `?status=${encodeURIComponent(status)}`;

    return this.http.get<DocumentFileDetails[]>(url).pipe(
      catchError(err => {
        console.error('[OpenAIChatService] getCompanyDocuments error:', err);
        return throwError(() => err);
      })
    );
  }

  extractDocumentData(
    companyId: number,
    file: File,
    options?: { documentType?: string; targetFields?: string; userMessage?: string }
  ): Observable<DocumentExtractionResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.documentType) formData.append('documentType', options.documentType);
    if (options?.targetFields) formData.append('targetFields', options.targetFields);
    if (options?.userMessage) formData.append('userMessage', options.userMessage);

    return this.http.post<DocumentExtractionResponse>(
      `${this.baseUrl}/api/company/${companyId}/DocumentAI/extract`,
      formData
    ).pipe(
      catchError(err => {
        console.error('[OpenAIChatService] extractDocumentData error:', err);
        return throwError(() => err);
      })
    );
  }

  deleteDocument(companyId: number, fileId: number): Observable<any> {
    return this.http.delete(
      `${this.baseUrl}/api/company/${companyId}/DocumentAI/files/${fileId}`
    );
  }

  markDocumentUsed(companyId: number, fileId: number, entityReference?: string): Observable<any> {
    let url = `${this.baseUrl}/api/company/${companyId}/DocumentAI/files/${fileId}/mark-used`;
    if (entityReference) url += `?entityReference=${encodeURIComponent(entityReference)}`;
    return this.http.post(url, null);
  }

  // ============================================================
  // SSE Streaming Helper (using native fetch)
  // ============================================================

  private async fetchStream(url: string, body: any, stream$: Subject<StreamChunkEvent>): Promise<void> {
    try {
      const token = sessionStorage.getItem('msal.idtoken')
        || sessionStorage.getItem('access_token')
        || '';

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => response.statusText);
        stream$.next({ type: 'error', error: `HTTP ${response.status}: ${errorText}` });
        stream$.complete();
        this._isStreaming$.next(false);
        return;
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value, { stream: true });
        const lines = text.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const chunk = line.slice(6);
            if (chunk === '[DONE]') {
              stream$.next({ type: 'done' });
              stream$.complete();
              this._isStreaming$.next(false);
              return;
            }
            stream$.next({ type: 'chunk', content: chunk });
          }
        }
      }

      stream$.next({ type: 'done' });
      stream$.complete();
    } catch (err: any) {
      stream$.next({ type: 'error', error: err?.message || 'Stream connection failed' });
      stream$.complete();
    } finally {
      this._isStreaming$.next(false);
    }
  }
}
